/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { defaultOptions, deserify } from "@karmaniverous/serify-deserify"
import {
  createSelector,
  createSlice,
  PayloadAction,
  type ActionReducerMapBuilder,
} from "@reduxjs/toolkit"
import { RootState } from "app/store"
import { submissionCommitted } from "features/userParameters/submissionCommitted"
import { Results } from "services/computations"
import { DeepReadonly } from "utils/readonly"

// AIDEV-NOTE: Keep reducer and selector inputs deeply readonly to satisfy strict correctness linting without changing slice behavior.

interface ResultsState {
  results: Results | null
  fresh: boolean | null
  // AIDEV-NOTE: (#1069) Distinct from `fresh: false`, which is the ordinary transient
  // "a recalculation is queued" state every slider step produces. This one says the
  // recalculation was *attempted and threw*, so the tables on screen answer a filter
  // selection the user has already moved away from and no further calculation is
  // coming. `fresh: false` alone cannot carry that: rendering a warning for it would
  // flash on every drag step.
  recalculationFailed: boolean
}

const initialState: ResultsState = {
  results: null,
  fresh: null,
  recalculationFailed: false,
}

// The state for tables a calculation has just produced. Shared by setResultsTables (the
// filter-driven recalculation) and submissionCommitted (a submission), which must agree.
const storeComputedResults = (results: DeepReadonly<Results>): ResultsState => ({
  results,
  fresh: true,
  // A calculation that produced tables clears the failure: these *are* the answer
  // to the current selection, so any warning about the previous one is now wrong.
  recalculationFailed: false,
})

const resultsSlice = createSlice({
  name: "results",
  initialState: initialState,
  reducers: {
    setResultsTables: (
      _state: DeepReadonly<ResultsState>,
      action: DeepReadonly<PayloadAction<DeepReadonly<Results>>>
    ): ResultsState => storeComputedResults(action.payload),
    setResultsFresh: (
      state: DeepReadonly<ResultsState>,
      action: DeepReadonly<PayloadAction<boolean>>
    ): ResultsState =>
      // AIDEV-NOTE: (#1458) An unchanged flag must leave the slice reference unchanged.
      // useTableRenderTracking dispatches setResultsFresh(true) from a requestAnimationFrame
      // after every DataTable reports rendered, and a new slice object here was one of the two
      // links (with selectUiTables below) that turned each dispatch into another full render
      // pass and another dispatch - an endless once-per-frame loop that crashed React with
      // "Maximum update depth exceeded". Returning the draft unmodified makes Immer hand back
      // the existing state.
      state.fresh === action.payload
        ? state
        : {
            results: state.results,
            fresh: action.payload,
            // Deliberately preserved. A failed recalculation leaves the tables wrong for the
            // current selection, and moving another slider does not make them right again -
            // only a calculation that completes does, via setResultsTables above.
            recalculationFailed: state.recalculationFailed,
          },
    // Records that a recalculation reached `computeResults` and threw. `fresh` is set
    // rather than preserved: whatever the flag happened to be, the rendered tables are
    // not the answer to the selection the user is looking at.
    setRecalculationFailed: (state: DeepReadonly<ResultsState>): ResultsState => ({
      results: state.results,
      fresh: false,
      recalculationFailed: true,
    }),
  },
  extraReducers: (builder: Readonly<ActionReducerMapBuilder<ResultsState>>) => {
    builder.addCase(
      submissionCommitted,
      (
        _state: DeepReadonly<ResultsState>,
        action: DeepReadonly<ReturnType<typeof submissionCommitted>>
      ): ResultsState => storeComputedResults(action.payload.results)
    )
  },
})

// Grab the raw, sentinel-laden payload
const selectRawResults = (state: DeepReadonly<RootState>): ResultsState => state.results

// AIDEV-NOTE: (#1458) Memoised on `state.results.results` alone, not on the slice object, so
// `uiTables` keeps its identity across setResultsFresh and setRecalculationFailed and changes
// only when setResultsTables delivers new results. That identity is what
// useTableRenderTracking treats as "a new result set arrived": a new one resets its counter,
// the DataTables report rendered again, and its requestAnimationFrame dispatches
// setResultsFresh(true). Memoised on the whole slice, that dispatch produced a new `uiTables`,
// which reset the counter, which scheduled the next dispatch - an endless once-per-frame loop
// that crashed React with "Maximum update depth exceeded". Same pattern as
// selectLastSubmittedUserParameters in app/hooks.ts.
const selectDeserializedResults = createSelector(
  (state: DeepReadonly<RootState>) => state.results.results,
  (serialized: DeepReadonly<Results> | null): Results | null => {
    if (serialized == null) {
      return null
    }
    // Safely handle the deserify result which returns any
    const deserialized: unknown = deserify(serialized as Results, defaultOptions)
    return deserialized as Results
  }
)

const selectResultsFreshFlag = (state: DeepReadonly<RootState>): boolean | null =>
  state.results.fresh

export const selectUiTables = createSelector(
  [selectDeserializedResults, selectResultsFreshFlag],
  (
    uiTables: DeepReadonly<Results> | null,
    fresh: boolean | null
  ): { uiTables: Results | null; fresh: boolean | null } =>
    uiTables == null ? { uiTables: null, fresh: fresh ?? null } : { uiTables, fresh }
)

// New selector to determine if calculation is pending based on !fresh
export const selectUiTablesCalculationPending = createSelector(
  selectRawResults,
  (raw: DeepReadonly<ResultsState>): boolean => raw.fresh === false
)

// Not a createSelector: it reads one boolean straight off the slice, so there is
// nothing to memoise and a memoised wrapper would only obscure that.
export const selectResultsRecalculationFailed = (state: DeepReadonly<RootState>): boolean =>
  selectRawResults(state).recalculationFailed

export const { setResultsTables, setResultsFresh, setRecalculationFailed } = resultsSlice.actions
export default resultsSlice.reducer
